import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Edit, Trash2, BookOpen, Clock, Eye, FileText, Loader2, Upload, FileArchive } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

interface ReadingMaterial {
  id: string;
  title: string;
  slug: string;
  grade: number;
  subject: string;
  topic: string;
  content: string;
  summary?: string | null;
  coverImageUrl?: string | null;
  estimatedReadTime: number;
  status: 'draft' | 'published' | 'archived';
  publishedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface Grade {
  id: string;
  gradeNumber: number;
  displayName: string;
  isActive: boolean;
  sortOrder: number;
}

interface Subject {
  id: string;
  gradeId: string;
  name: string;
  displayName: string;
  isActive: boolean;
  sortOrder: number;
}

interface Topic {
  id: string;
  subjectId: string;
  name: string;
  displayName: string;
  isActive: boolean;
  sortOrder: number;
}

type MaterialStatus = 'draft' | 'published' | 'archived';

interface FormData {
  title: string;
  grade: string;
  subject: string;
  topic: string;
  content: string;
  summary: string;
  coverImageUrl: string;
  estimatedReadTime: number;
  status: MaterialStatus;
}

const emptyFormData: FormData = {
  title: '',
  grade: '',
  subject: '',
  topic: '',
  content: '',
  summary: '',
  coverImageUrl: '',
  estimatedReadTime: 5,
  status: 'draft'
};

interface ZipFormData {
  title: string;
  grade: string;
  subject: string;
  topic: string;
  summary: string;
  estimatedReadTime: number;
  status: MaterialStatus;
}

const emptyZipFormData: ZipFormData = {
  title: '',
  grade: '',
  subject: '',
  topic: '',
  summary: '',
  estimatedReadTime: 5,
  status: 'draft'
};

export default function ReadingMaterialsManager() {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState<ReadingMaterial | null>(null);
  const [formData, setFormData] = useState(emptyFormData);
  const [filterGrade, setFilterGrade] = useState<string>('all');
  const [filterSubject, setFilterSubject] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  
  const [zipDialogOpen, setZipDialogOpen] = useState(false);
  const [zipFile, setZipFile] = useState<File | null>(null);
  const [zipFormData, setZipFormData] = useState(emptyZipFormData);
  const [isUploading, setIsUploading] = useState(false);

  const { data: materials = [], isLoading } = useQuery<ReadingMaterial[]>({
    queryKey: ['/api/admin/reading-materials', filterGrade, filterSubject, filterStatus],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filterGrade !== 'all') params.append('grade', filterGrade);
      if (filterSubject !== 'all') params.append('subject', filterSubject);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      const response = await fetch(`/api/admin/reading-materials?${params}`, { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch reading materials');
      return response.json();
    }
  });

  const { data: grades = [] } = useQuery<Grade[]>({
    queryKey: ['/api/admin/grades'],
  });

  const { data: subjects = [] } = useQuery<Subject[]>({
    queryKey: ['/api/admin/subjects'],
  });

  const { data: topics = [] } = useQuery<Topic[]>({
    queryKey: ['/api/admin/topics'],
  });

  const activeGrades = grades.filter(g => g.isActive).sort((a, b) => a.sortOrder - b.sortOrder);
  const activeSubjects = subjects.filter(s => s.isActive).sort((a, b) => a.sortOrder - b.sortOrder);
  
  const selectedGrade = activeGrades.find(g => g.gradeNumber.toString() === formData.grade);
  const gradeSubjects = activeSubjects.filter(s => s.gradeId === selectedGrade?.id);
  
  const selectedSubject = gradeSubjects.find(s => s.name === formData.subject);
  const subjectTopics = topics.filter(t => t.subjectId === selectedSubject?.id && t.isActive);

  const zipSelectedGrade = activeGrades.find(g => g.gradeNumber.toString() === zipFormData.grade);
  const zipGradeSubjects = activeSubjects.filter(s => s.gradeId === zipSelectedGrade?.id);
  const zipSelectedSubject = zipGradeSubjects.find(s => s.name === zipFormData.subject);
  const zipSubjectTopics = topics.filter(t => t.subjectId === zipSelectedSubject?.id && t.isActive);

  const resetZipForm = () => {
    setZipFormData(emptyZipFormData);
    setZipFile(null);
    setZipDialogOpen(false);
  };

  const handleZipUpload = async () => {
    if (!zipFile) {
      toast({ title: 'Error', description: 'Please select a zip file', variant: 'destructive' });
      return;
    }
    if (!zipFormData.title || !zipFormData.grade || !zipFormData.subject || !zipFormData.topic) {
      toast({ title: 'Error', description: 'Please fill in all required fields', variant: 'destructive' });
      return;
    }

    setIsUploading(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('zipFile', zipFile);
      formDataToSend.append('title', zipFormData.title);
      formDataToSend.append('grade', zipFormData.grade);
      formDataToSend.append('subject', zipFormData.subject);
      formDataToSend.append('topic', zipFormData.topic);
      formDataToSend.append('summary', zipFormData.summary);
      formDataToSend.append('estimatedReadTime', zipFormData.estimatedReadTime.toString());
      formDataToSend.append('status', zipFormData.status);

      const response = await fetch('/api/admin/reading-materials/upload-zip', {
        method: 'POST',
        credentials: 'include',
        body: formDataToSend
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to upload zip file');
      }

      const result = await response.json();
      queryClient.invalidateQueries({ queryKey: ['/api/admin/reading-materials'] });
      toast({ 
        title: 'Success', 
        description: `Reading material created with ${result.uploadedImages} images` 
      });
      resetZipForm();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message || 'Failed to upload zip file', variant: 'destructive' });
    } finally {
      setIsUploading(false);
    }
  };

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const response = await apiRequest('POST', '/api/admin/reading-materials', data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/reading-materials'] });
      toast({ title: 'Success', description: 'Reading material created successfully' });
      resetForm();
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to create reading material', variant: 'destructive' });
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: typeof formData }) => {
      const response = await apiRequest('PUT', `/api/admin/reading-materials/${id}`, data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/reading-materials'] });
      toast({ title: 'Success', description: 'Reading material updated successfully' });
      resetForm();
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to update reading material', variant: 'destructive' });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest('DELETE', `/api/admin/reading-materials/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/reading-materials'] });
      toast({ title: 'Success', description: 'Reading material deleted successfully' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to delete reading material', variant: 'destructive' });
    }
  });

  const resetForm = () => {
    setFormData(emptyFormData);
    setEditingMaterial(null);
    setDialogOpen(false);
  };

  const handleEdit = (material: ReadingMaterial) => {
    setEditingMaterial(material);
    setFormData({
      title: material.title,
      grade: material.grade.toString(),
      subject: material.subject,
      topic: material.topic,
      content: material.content,
      summary: material.summary || '',
      coverImageUrl: material.coverImageUrl || '',
      estimatedReadTime: material.estimatedReadTime,
      status: material.status
    });
    setDialogOpen(true);
  };

  const handleSubmit = () => {
    if (!formData.title || !formData.grade || !formData.subject || !formData.topic || !formData.content) {
      toast({ title: 'Error', description: 'Please fill in all required fields', variant: 'destructive' });
      return;
    }
    
    if (editingMaterial) {
      updateMutation.mutate({ id: editingMaterial.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'published':
        return <Badge className="bg-green-500">Published</Badge>;
      case 'draft':
        return <Badge variant="secondary">Draft</Badge>;
      case 'archived':
        return <Badge variant="outline">Archived</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Reading Materials</h2>
          <p className="text-muted-foreground">Manage lesson content for students to read</p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setZipDialogOpen(true)}>
            <FileArchive className="w-4 h-4 mr-2" />
            Upload from Zip
          </Button>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { resetForm(); setDialogOpen(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Add Reading Material
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingMaterial ? 'Edit' : 'Create'} Reading Material</DialogTitle>
              <DialogDescription>
                {editingMaterial ? 'Update the reading content below.' : 'Create new reading content for students.'}
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Enter title..."
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="estimatedReadTime">Est. Read Time (minutes)</Label>
                  <Input
                    id="estimatedReadTime"
                    type="number"
                    value={formData.estimatedReadTime}
                    onChange={(e) => setFormData({ ...formData, estimatedReadTime: parseInt(e.target.value) || 5 })}
                    min={1}
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Grade *</Label>
                  <Select value={formData.grade} onValueChange={(value) => setFormData({ ...formData, grade: value, subject: '', topic: '' })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select grade" />
                    </SelectTrigger>
                    <SelectContent>
                      {activeGrades.map((g) => (
                        <SelectItem key={g.id} value={g.gradeNumber.toString()}>
                          {g.displayName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Subject *</Label>
                  <Select 
                    value={formData.subject} 
                    onValueChange={(value) => setFormData({ ...formData, subject: value, topic: '' })}
                    disabled={!formData.grade}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select subject" />
                    </SelectTrigger>
                    <SelectContent>
                      {gradeSubjects.map((s) => (
                        <SelectItem key={s.id} value={s.name}>
                          {s.displayName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Topic *</Label>
                  <Select 
                    value={formData.topic} 
                    onValueChange={(value) => setFormData({ ...formData, topic: value })}
                    disabled={!formData.subject}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select topic" />
                    </SelectTrigger>
                    <SelectContent>
                      {subjectTopics.map((t) => (
                        <SelectItem key={t.id} value={t.name}>
                          {t.displayName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="summary">Summary (optional)</Label>
                <Textarea
                  id="summary"
                  value={formData.summary}
                  onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                  placeholder="Brief description of this reading material..."
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="content">Content * (HTML supported)</Label>
                <Textarea
                  id="content"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Enter the reading content here... HTML tags are supported."
                  rows={12}
                  className="font-mono text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="coverImageUrl">Cover Image URL (optional)</Label>
                  <Input
                    id="coverImageUrl"
                    value={formData.coverImageUrl}
                    onChange={(e) => setFormData({ ...formData, coverImageUrl: e.target.value })}
                    placeholder="https://..."
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={formData.status} onValueChange={(value: 'draft' | 'published' | 'archived') => setFormData({ ...formData, status: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="published">Published</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={resetForm}>Cancel</Button>
                <Button 
                  onClick={handleSubmit}
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  {(createMutation.isPending || updateMutation.isPending) && (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  )}
                  {editingMaterial ? 'Update' : 'Create'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Dialog open={zipDialogOpen} onOpenChange={setZipDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Upload Reading Material from Zip</DialogTitle>
            <DialogDescription>
              Upload a zip file containing an HTML file and images. The content will be extracted and images will be uploaded automatically.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="zipFile">Zip File *</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="zipFile"
                  type="file"
                  accept=".zip"
                  onChange={(e) => setZipFile(e.target.files?.[0] || null)}
                  className="flex-1"
                />
                {zipFile && (
                  <Badge variant="secondary">
                    <FileArchive className="w-3 h-3 mr-1" />
                    {zipFile.name}
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Zip should contain an index.html file and any image assets
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="zipTitle">Title *</Label>
                <Input
                  id="zipTitle"
                  value={zipFormData.title}
                  onChange={(e) => setZipFormData({ ...zipFormData, title: e.target.value })}
                  placeholder="Enter title..."
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="zipEstimatedReadTime">Est. Read Time (minutes)</Label>
                <Input
                  id="zipEstimatedReadTime"
                  type="number"
                  value={zipFormData.estimatedReadTime}
                  onChange={(e) => setZipFormData({ ...zipFormData, estimatedReadTime: parseInt(e.target.value) || 5 })}
                  min={1}
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Grade *</Label>
                <Select value={zipFormData.grade} onValueChange={(value) => setZipFormData({ ...zipFormData, grade: value, subject: '', topic: '' })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select grade" />
                  </SelectTrigger>
                  <SelectContent>
                    {activeGrades.map((g) => (
                      <SelectItem key={g.id} value={g.gradeNumber.toString()}>
                        {g.displayName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Subject *</Label>
                <Select 
                  value={zipFormData.subject} 
                  onValueChange={(value) => setZipFormData({ ...zipFormData, subject: value, topic: '' })}
                  disabled={!zipFormData.grade}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select subject" />
                  </SelectTrigger>
                  <SelectContent>
                    {zipGradeSubjects.map((s) => (
                      <SelectItem key={s.id} value={s.name}>
                        {s.displayName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Topic *</Label>
                <Select 
                  value={zipFormData.topic} 
                  onValueChange={(value) => setZipFormData({ ...zipFormData, topic: value })}
                  disabled={!zipFormData.subject}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select topic" />
                  </SelectTrigger>
                  <SelectContent>
                    {zipSubjectTopics.map((t) => (
                      <SelectItem key={t.id} value={t.name}>
                        {t.displayName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="zipSummary">Summary (optional)</Label>
              <Textarea
                id="zipSummary"
                value={zipFormData.summary}
                onChange={(e) => setZipFormData({ ...zipFormData, summary: e.target.value })}
                placeholder="Brief description of this reading material..."
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={zipFormData.status} onValueChange={(value: 'draft' | 'published' | 'archived') => setZipFormData({ ...zipFormData, status: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={resetZipForm}>Cancel</Button>
              <Button 
                onClick={handleZipUpload}
                disabled={isUploading}
              >
                {isUploading && (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                )}
                <Upload className="w-4 h-4 mr-2" />
                Upload
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <Select value={filterGrade} onValueChange={setFilterGrade}>
              <SelectTrigger>
                <SelectValue placeholder="All Grades" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Grades</SelectItem>
                {activeGrades.map((g) => (
                  <SelectItem key={g.id} value={g.gradeNumber.toString()}>
                    {g.displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filterSubject} onValueChange={setFilterSubject}>
              <SelectTrigger>
                <SelectValue placeholder="All Subjects" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Subjects</SelectItem>
                {activeSubjects.map((s) => (
                  <SelectItem key={s.id} value={s.name}>
                    {s.displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger>
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="published">Published</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            Reading Materials ({materials.length})
          </CardTitle>
          <CardDescription>
            Manage reading content for each grade, subject, and topic
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : materials.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No reading materials found. Create your first one!</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Grade</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Topic</TableHead>
                  <TableHead>Read Time</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {materials.map((material) => (
                  <TableRow key={material.id}>
                    <TableCell className="font-medium max-w-[200px] truncate">
                      {material.title}
                    </TableCell>
                    <TableCell>Grade {material.grade}</TableCell>
                    <TableCell className="capitalize">{material.subject}</TableCell>
                    <TableCell className="capitalize">{material.topic}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {material.estimatedReadTime} min
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(material.status)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(material)}
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            if (confirm('Are you sure you want to delete this reading material?')) {
                              deleteMutation.mutate(material.id);
                            }
                          }}
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
